import React, { useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { Link, useLocation } from 'react-router-dom'
import { useTran } from 'src/i18n/useTran'
import { msg } from 'src/i18n/texts/keys'
import { useTheme } from 'src/ui/style/useTheme'
import { pages } from 'src/routing/pages'
import { isMobileScreen } from 'src/ui/style/ScreenSize'
import {
    buildFbpixelPayload,
    buildGadsPayload,
    gtmEvent,
    isMarketingCookieEnabled
} from 'src/analytics/gtm'
import { deleteCookie, getCookie, setCookie } from 'src/utils/utils'
import { COOKIE_INVENTORY } from 'src/legal/cookieInventory'
import {
    closeCookieBar,
    cookieBarVisibility,
    openCookieBar
} from './cookieBarStore'

const marketingCookieName = 'marketing'
const essentialCookieName = 'essential'
const COOKIE_EXPIRY_DAYS = 365
const reopenAfterInfoKey = 'cookie-bar-reopen-after-info'
const consentStorageKey = 'cookie-consent'
const essentialConsentStorageKey = 'cookie-consent-essential'
const marketingConsentStorageKey = 'cookie-consent-marketing'

type ConsentState = {
    essential: boolean
    marketing: boolean
}

const readStorage = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key)
    } catch {
        return null
    }
}

const writeStorage = (key: string, value: string) => {
    try {
        window.localStorage.setItem(key, value)
    } catch {}
}

const parseConsentBool = (value: string | null): boolean | null => {
    if (value === 'true') return true
    if (value === 'false') return false
    return null
}

const getStoredEssentialConsent = (): boolean | null => {
    const cookieValue = parseConsentBool(getCookie(essentialCookieName))
    if (cookieValue !== null) return cookieValue

    const storageValue = parseConsentBool(
        readStorage(essentialConsentStorageKey)
    )
    if (storageValue !== null) return storageValue

    return parseConsentBool(readStorage(consentStorageKey))
}

const getStoredMarketingConsent = (): boolean | null => {
    const cookieValue = parseConsentBool(getCookie(marketingCookieName))
    if (cookieValue !== null) return cookieValue

    return parseConsentBool(readStorage(marketingConsentStorageKey))
}

const getInitialSelection = (): ConsentState => {
    const storedEssential = getStoredEssentialConsent()
    const storedMarketing = getStoredMarketingConsent()
    const isFirstVisit = storedEssential === null && storedMarketing === null

    if (isFirstVisit) {
        return { essential: false, marketing: false }
    }

    return {
        essential: storedEssential === true,
        marketing: storedMarketing === true
    }
}

const marketingCookies = COOKIE_INVENTORY.filter(
    cookie =>
        cookie.type === 'cookie' &&
        cookie.category === 'non-essential' &&
        cookie.removeOnReject !== false
)

export const CookieBar = () => {
    const t = useTran()
    const th = useTheme()
    const isMobile = isMobileScreen()
    const pageVisitSent = useRef(false)
    const location = useLocation()

    const [selection, setSelection] = useState<ConsentState>(() =>
        getInitialSelection()
    )
    const [isOpen, setIsOpen] = useState<boolean>(() => {
        const storedEssential = getStoredEssentialConsent()
        return cookieBarVisibility.value() || storedEssential !== true
    })
    const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
    const [categoryOpen, setCategoryOpen] = useState<{
        essential: boolean
        'non-essential': boolean
    }>({
        essential: false,
        'non-essential': false
    })
    const [showSettings, setShowSettings] = useState(false)

    useEffect(() => {
        const subscription = cookieBarVisibility
            .changes()
            .subscribe(value => setIsOpen(value))

        return () => subscription.unsubscribe()
    }, [])

    useEffect(() => {
        if (location.pathname === pages.Gdpr.to()) return

        const storedEssential = getStoredEssentialConsent()
        const shouldReopen =
            sessionStorage.getItem(reopenAfterInfoKey) === 'true'

        if (storedEssential !== true && shouldReopen) {
            sessionStorage.removeItem(reopenAfterInfoKey)
            openCookieBar()
        }
    }, [location.pathname])

    const emitPageVisit = (attempt = 0, maxAttempts = 10) => {
        if (pageVisitSent.current) return

        const marketingAllowed = isMarketingCookieEnabled()
        const hasDataLayer = Array.isArray(window.dataLayer)
        const gclid = getCookie('gclid')
        const fbclid = getCookie('fbclid')
        const hasClickId = Boolean(gclid) || Boolean(fbclid)

        if (
            attempt < maxAttempts &&
            (!marketingAllowed || !hasDataLayer || !hasClickId)
        ) {
            setTimeout(() => emitPageVisit(attempt + 1, maxAttempts), 1000)
            return
        }

        if (!marketingAllowed || !hasDataLayer) return

        const payload: Record<string, string> = {
            path: window.location.pathname,
            url: window.location.href,
            gads: buildGadsPayload(),
            fbpixel: buildFbpixelPayload()
        }

        gtmEvent('page_visit', payload)
        pageVisitSent.current = true
    }

    const removeOneCookie = (
        cookieName: string,
        domain?: string,
        topLevelDomain?: string,
        path?: string
    ) => {
        deleteCookie(cookieName, domain, path)
        deleteCookie(cookieName, topLevelDomain, path)
        deleteCookie(cookieName)
    }

    const removeNotPermittedCookies = () => {
        const domain = window.location.hostname
        const topLevelDomain = `.${domain.split('.').slice(-2).join('.')}`
        const path = '/'

        marketingCookies.forEach(entry => {
            if (entry.match === 'prefix') {
                document.cookie
                    .split('; ')
                    .map(c => c.split('=')[0])
                    .filter(name => name.startsWith(entry.key))
                    .forEach(name =>
                        removeOneCookie(name, domain, topLevelDomain, path)
                    )
                return
            }

            removeOneCookie(entry.key, domain, topLevelDomain, path)
        })
    }

    const gtmSavePreferences = (enabled: boolean) => {
        const consentState = enabled ? 'granted' : 'denied'
        window.gtag?.('consent', 'update', {
            ad_storage: consentState,
            analytics_storage: consentState,
            personalization_storage: consentState,
            ad_user_data: consentState,
            ad_personalization: consentState
        })
        gtmEvent('consent-update')

        if (enabled) {
            emitPageVisit()
        }
    }

    const updateGtmPreferences = (next: ConsentState) => {
        if (next.marketing) {
            gtmSavePreferences(true)
        } else {
            removeNotPermittedCookies()
            gtmSavePreferences(false)
        }
    }

    const persistConsent = (next: ConsentState, closeAfter = true) => {
        const dataLayerPayload = [
            { name: essentialCookieName, checked: next.essential },
            { name: marketingCookieName, checked: next.marketing }
        ]

        setCookie(
            essentialCookieName,
            next.essential ? 'true' : 'false',
            COOKIE_EXPIRY_DAYS
        )
        setCookie(
            marketingCookieName,
            next.marketing ? 'true' : 'false',
            COOKIE_EXPIRY_DAYS
        )
        writeStorage(consentStorageKey, next.essential ? 'true' : 'false')
        writeStorage(
            essentialConsentStorageKey,
            next.essential ? 'true' : 'false'
        )
        writeStorage(
            marketingConsentStorageKey,
            next.marketing ? 'true' : 'false'
        )

        updateGtmPreferences(next)
        gtmEvent('consent-update-detail', {
            cookies: dataLayerPayload
        })

        if (closeAfter) {
            closeCookieBar()
            setIsOpen(false)
        }
    }

    useEffect(() => {
        const stored = getInitialSelection()
        setSelection(stored)
        updateGtmPreferences(stored)
        emitPageVisit()
    }, [])

    const essentialItems = useMemo(
        () => [
            t(msg.ui.cookieBar.essential.includes.cookies),
            t(msg.ui.cookieBar.essential.includes.map)
        ],
        [t]
    )
    const nonEssentialItems = useMemo(
        () => [
            t(msg.ui.cookieBar.nonEssential.includes.analytics),
            t(msg.ui.cookieBar.nonEssential.includes.ads),
            t(msg.ui.cookieBar.nonEssential.includes.behaviour)
        ],
        [t]
    )

    const isGdprPage = location.pathname === pages.Gdpr.to()
    const isManualOpen = cookieBarVisibility.value()

    if (!isOpen) return null
    if (isGdprPage && !isManualOpen) return null

    const saveDisabled = !selection.essential

    const handleSaveSelection = () => persistConsent(selection)
    const handleOpenSettings = () => setShowSettings(true)
    const handleCloseSettings = () => setShowSettings(false)

    const handleAcceptAll = () => {
        const allEnabled: ConsentState = { essential: true, marketing: true }
        setSelection(allEnabled)
        persistConsent(allEnabled)
    }

    const toggleSelection = (field: keyof ConsentState) =>
        setSelection(prev => ({ ...prev, [field]: !prev[field] }))

    const itemsByCategory = COOKIE_INVENTORY.reduce<
        Record<'essential' | 'non-essential', typeof COOKIE_INVENTORY>
    >(
        (acc, item) => {
            acc[item.category].push(item)
            return acc
        },
        { essential: [], 'non-essential': [] }
    )

    const toggleItem = (key: string) =>
        setOpenItems(prev => ({ ...prev, [key]: !prev[key] }))

    const toggleCategory = (cat: 'essential' | 'non-essential') =>
        setCategoryOpen(prev => ({ ...prev, [cat]: !prev[cat] }))

    const ItemList = ({
        category
    }: {
        category: 'essential' | 'non-essential'
    }) => (
        <div
            css={css`
                display: grid;
                max-height: ${isMobile ? '26vh' : '40vh'};
                overflow: auto;
                padding: ${th.spacing.sm};
                background: ${th.colors.lightBlue};
                border: 1px solid ${th.colors.primary};
                border-radius: 0;
            `}
        >
            {itemsByCategory[category].map(item => {
                const isOpenItem = openItems[item.key]
                const i18nKey = item.i18nKey || item.key
                const labelKey = (msg.ui.cookieBar.inventory as any)?.[i18nKey]
                    ?.label
                const purposeKey = (msg.ui.cookieBar.inventory as any)?.[
                    i18nKey
                ]?.purpose
                const expiresKey = (msg.ui.cookieBar.inventory as any)?.[
                    i18nKey
                ]?.expires
                const itemLabel = labelKey ? t(labelKey) : item.label
                const itemPurpose = purposeKey ? t(purposeKey) : item.purpose
                const itemExpires = expiresKey ? t(expiresKey) : item.expires
                return (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => toggleItem(item.key)}
                        css={css`
                            text-align: left;
                            background: ${th.colors.white};
                            border: 1px solid ${th.colors.lightGray};
                            border-radius: 0;
                            padding: ${th.spacing.sm};
                            box-shadow: ${th.boxShadow.bottomOnly};
                            cursor: pointer;
                        `}
                    >
                        <div
                            css={css`
                                display: flex;
                                justify-content: space-between;
                                align-items: center;
                                gap: ${th.spacing.sm};
                                color: ${th.colors.primary};
                                font-weight: ${th.fontWeight.bold};
                            `}
                        >
                            <span>
                                {itemLabel}{' '}
                                <span
                                    css={css`
                                        color: ${th.colors.darkGray};
                                        font-weight: ${th.fontWeight.normal};
                                    `}
                                >
                                    (
                                    {item.match === 'prefix'
                                        ? `${item.key}*`
                                        : item.key}
                                    )
                                </span>
                            </span>
                            <span
                                css={css`
                                    font-size: ${th.fontSize.sm};
                                    color: ${th.colors.darkGray};
                                `}
                            >
                                {item.type === 'cookie' &&
                                    t(msg.ui.cookieBar.detail.type.cookie)}
                                {item.type === 'localStorage' &&
                                    t(
                                        msg.ui.cookieBar.detail.type
                                            .localStorage
                                    )}
                                {item.type === 'sessionStorage' &&
                                    t(
                                        msg.ui.cookieBar.detail.type
                                            .sessionStorage
                                    )}
                            </span>
                        </div>
                        {isOpenItem && (
                            <div
                                css={css`
                                    margin-top: ${th.spacing.sm};
                                    color: ${th.colors.darkGray};
                                    font-size: ${th.fontSize.sm};
                                `}
                            >
                                <div>{itemPurpose}</div>
                                <div
                                    css={css`
                                        margin-top: ${th.spacing.sm};
                                        color: ${th.colors.gray};
                                    `}
                                >
                                    {t(msg.ui.cookieBar.detail.columns.expires)}
                                    : {itemExpires}
                                </div>
                            </div>
                        )}
                    </button>
                )
            })}
        </div>
    )

    const SectionCard = ({
        title,
        text,
        items,
        checked,
        onChange,
        accent,
        detailButton,
        background,
        titleColor,
        textColor
    }: {
        title: string
        text: string
        items: string[]
        checked: boolean
        onChange: () => void
        accent: string
        detailButton?: React.ReactNode
        background?: string
        titleColor?: string
        textColor?: string
    }) => (
        <label
            css={css`
                display: grid;
                grid-template-columns: auto 1fr;
                gap: ${th.spacing.md};
                padding: ${th.spacing.md};
                background: ${background || 'rgba(217, 244, 255, 0.6)'};
                border: 1px solid ${accent};
                border-left: 6px solid ${accent};
                border-radius: 0;
                box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
                cursor: pointer;
            `}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                css={css`
                    width: 20px;
                    height: 20px;
                    margin-top: ${th.spacing.sm};
                    appearance: none;
                    border: 2px solid ${th.colors.tertiary};
                    background: ${th.colors.white};
                    position: relative;
                    box-sizing: border-box;
                    cursor: pointer;

                    &:checked {
                        background: rgba(217, 244, 255, 0.7);
                    }

                    &:checked::after {
                        content: '';
                        width: 12px;
                        height: 7px;
                        border-left: 2px solid ${th.colors.tertiary};
                        border-bottom: 2px solid ${th.colors.tertiary};
                        position: absolute;
                        left: 50%;
                        top: 50%;
                        transform: translate(-50%, -55%) rotate(-45deg);
                    }
                `}
            />
            <div>
                <div
                    css={css`
                        font-weight: ${th.fontWeight.bold};
                        margin-bottom: ${th.spacing.sm};
                        color: ${titleColor || th.colors.primary};
                    `}
                >
                    {title}
                </div>
                <p
                    css={css`
                        margin: 0 0 ${th.spacing.sm} 0;
                        color: ${textColor || th.colors.darkGray};
                    `}
                >
                    {text}
                </p>
                <ul
                    css={css`
                        margin: 0;
                        padding-left: ${th.spacing.md};
                        color: ${textColor || th.colors.darkGray};
                        display: grid;
                        gap: ${th.spacing.sm};
                        list-style: none;
                    `}
                >
                    {items.map(item => (
                        <li
                            key={item}
                            css={css`
                                display: flex;
                                gap: ${th.spacing.sm};
                                align-items: flex-start;
                            `}
                        >
                            <span
                                css={css`
                                    color: ${textColor || th.colors.darkGray};
                                    margin-top: 2px;
                                    font-size: ${th.fontSize.xs};
                                `}
                            >
                                –
                            </span>
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
                {detailButton && (
                    <div
                        css={css`
                            display: flex;
                            justify-content: flex-end;
                            margin-top: ${th.spacing.sm};
                        `}
                    >
                        {detailButton}
                    </div>
                )}
            </div>
        </label>
    )

    return (
        <div
            css={css`
                position: fixed;
                inset: auto 0 0 0;
                z-index: 1000;
                pointer-events: none;
                display: flex;
                justify-content: center;
                margin: 0;
                color: ${th.colors.black};
                box-shadow: none;
            `}
        >
            <div
                css={css`
                    max-width: 1200px;
                    margin: 0 auto;
                    background: ${th.colors.white};
                    border: 1px solid ${th.colors.primary};
                    padding: ${th.spacing.md};
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: ${th.spacing.lg};
                    box-shadow: ${th.boxShadow.around};
                    pointer-events: auto;
                    max-height: 90vh;
                    overflow-y: auto;
                    overscroll-behavior: contain;
                `}
            >
                <div
                    css={css`
                        display: grid;
                        gap: ${th.spacing.md};
                    `}
                >
                    <div>
                        <h3
                            css={css`
                                margin: 0 0 ${th.spacing.sm} 0;
                                font-size: ${th.fontSize.md};
                                font-weight: ${th.fontWeight.bold};
                                color: ${th.colors.tertiary};
                            `}
                        >
                            {t(msg.ui.cookieBar.title)}
                        </h3>
                        <p
                            css={css`
                                margin: 0;
                                color: ${th.colors.black};
                                font-size: ${isMobile
                                    ? th.fontSize.sm
                                    : th.fontSize.md};
                            `}
                        >
                            {t(msg.ui.cookieBar.text)}{' '}
                            <Link
                                to={pages.Gdpr.to()}
                                css={css`
                                    color: ${th.colors.tertiary};
                                    text-decoration: underline;
                                    font-weight: ${th.fontWeight.bold};
                                `}
                                onClick={() => {
                                    sessionStorage.setItem(
                                        reopenAfterInfoKey,
                                        'true'
                                    )
                                    closeCookieBar()
                                }}
                            >
                                {t(msg.ui.cookieBar.moreInformation)}
                            </Link>
                        </p>
                    </div>

                    {showSettings && (
                        <div
                            css={css`
                                display: grid;
                                gap: ${th.spacing.md};
                            `}
                        >
                            <SectionCard
                                title={t(msg.ui.cookieBar.essential.title)}
                                text={t(msg.ui.cookieBar.essential.text)}
                                items={essentialItems}
                                checked={selection.essential}
                                onChange={() => toggleSelection('essential')}
                                accent={th.colors.primary}
                                detailButton={
                                    <button
                                        type="button"
                                        onClick={() =>
                                            toggleCategory('essential')
                                        }
                                        css={css`
                                            background: ${th.colors.white};
                                            color: ${th.colors.primary};
                                            border: 1px solid
                                                ${th.colors.primary};
                                            padding: ${th.spacing.sm}
                                                ${th.spacing.sm};
                                            border-radius: 0;
                                            cursor: pointer;
                                            font-weight: ${th.fontWeight.bold};
                                        `}
                                    >
                                        {categoryOpen.essential
                                            ? t(msg.ui.cookieBar.detail.hide)
                                            : t(msg.ui.cookieBar.detail.show)}
                                    </button>
                                }
                            />
                            {categoryOpen.essential && (
                                <ItemList category="essential" />
                            )}
                            <SectionCard
                                title={t(msg.ui.cookieBar.nonEssential.title)}
                                text={t(msg.ui.cookieBar.nonEssential.text)}
                                items={nonEssentialItems}
                                checked={selection.marketing}
                                onChange={() => toggleSelection('marketing')}
                                accent={th.colors.primary}
                                detailButton={
                                    <button
                                        type="button"
                                        onClick={() =>
                                            toggleCategory('non-essential')
                                        }
                                        css={css`
                                            background: ${th.colors.white};
                                            color: ${th.colors.primary};
                                            border: 1px solid
                                                ${th.colors.primary};
                                            padding: ${th.spacing.sm}
                                                ${th.spacing.sm};
                                            border-radius: 0;
                                            cursor: pointer;
                                            font-weight: ${th.fontWeight.bold};
                                        `}
                                    >
                                        {categoryOpen['non-essential']
                                            ? t(msg.ui.cookieBar.detail.hide)
                                            : t(msg.ui.cookieBar.detail.show)}
                                    </button>
                                }
                            />
                            {categoryOpen['non-essential'] && (
                                <ItemList category="non-essential" />
                            )}
                        </div>
                    )}

                    <div
                        css={css`
                            display: flex;
                            gap: ${th.spacing.md};
                            flex-wrap: wrap;
                            justify-content: space-between;
                            padding: ${isMobile ? `${th.spacing.sm} 0` : 0};
                        `}
                    >
                        <div>
                            {showSettings && (
                                <button
                                    type="button"
                                    onClick={handleSaveSelection}
                                    disabled={saveDisabled}
                                    css={css`
                                        background: ${th.colors.white};
                                        color: ${th.colors.primary};
                                        border: 1px solid ${th.colors.primary};
                                        padding: ${th.spacing.sm}
                                            ${th.spacing.md};
                                        border-radius: 0;
                                        font-weight: ${th.fontWeight.bold};
                                        opacity: ${saveDisabled ? 0.6 : 1};
                                        cursor: ${saveDisabled
                                            ? 'not-allowed'
                                            : 'pointer'};
                                    `}
                                >
                                    {t(msg.ui.cookieBar.save)}
                                </button>
                            )}
                        </div>
                        <div
                            css={css`
                                display: flex;
                                gap: ${th.spacing.md};
                                flex-wrap: wrap;
                                justify-content: ${isMobile
                                    ? 'center'
                                    : 'flex-end'};
                            `}
                        >
                            {showSettings ? (
                                <button
                                    type="button"
                                    onClick={handleCloseSettings}
                                    css={css`
                                        background: ${th.colors.secondary};
                                        color: ${th.colors.white};
                                        border: none;
                                        padding: ${th.spacing.sm}
                                            ${th.spacing.md};
                                        border-radius: 0;
                                        font-weight: ${th.fontWeight.bold};
                                        cursor: pointer;
                                    `}
                                >
                                    {t(msg.ui.back)}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleOpenSettings}
                                    css={css`
                                        background: ${th.colors.secondary};
                                        color: ${th.colors.white};
                                        border: none;
                                        padding: ${th.spacing.sm}
                                            ${th.spacing.md};
                                        border-radius: 0;
                                        font-weight: ${th.fontWeight.bold};
                                        cursor: pointer;
                                    `}
                                >
                                    {t(msg.ui.cookieBar.preferences)}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handleAcceptAll}
                                css={css`
                                    background: ${th.colors.tertiary};
                                    color: ${th.colors.white};
                                    border: none;
                                    padding: ${th.spacing.sm} ${th.spacing.md};
                                    border-radius: 0;
                                    font-weight: ${th.fontWeight.bold};
                                    cursor: pointer;
                                `}
                            >
                                {t(msg.ui.cookieBar.acceptAll)}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
